import { TAB_GROUPS, subtitleForTab, titleForTab, type Tab } from "../navigation.ts";

/** Feature tabs documented in Help (excludes the Help tab itself). */
export type FeatureTab = Exclude<Tab, "help">;

/** Help-only topics (no Fleet Control nav tab). */
export type HelpOnlyTopicId =
  | "fleet-architecture"
  | "fleet-session-environments"
  | "fleet-projects"
  | "fleet-self-improvement"
  | "fleet-self-improvement-local"
  | "fleet-work-plan"
  | "fleet-stop-resume";

export type HelpTopicId = "intro" | HelpOnlyTopicId | FeatureTab;

const HELP_ONLY_TOPIC_IDS = new Set<string>([
  "fleet-architecture",
  "fleet-session-environments",
  "fleet-projects",
  "fleet-self-improvement",
  "fleet-self-improvement-local",
  "fleet-work-plan",
  "fleet-stop-resume",
]);

export type HelpArticleImage = {
  /** Basename under `docs/BIBLE/`, resolved by `bibleScreenshotUrl`. */
  file: string;
  alt: string;
};

export interface HelpArticle {
  id: HelpTopicId;
  group: string;
  title: string;
  tagline: string;
  overview: string;
  steps: string[];
  tips: string[];
  /** Opens the corresponding Fleet Control tab when set. */
  openTab?: FeatureTab;
  /** Existing BIBLE screenshots for this topic (S-16.1 / O-14). */
  images?: HelpArticleImage[];
}

/**
 * The tab ids that actually appear in `TAB_GROUPS`. `TAB_GROUPS` is
 * `as const`, so each group's `tabs` element type is a narrower literal
 * union than the full `Tab`.
 *
 * JAISIU-types-sweep: the filters below used to be typed
 * `t is FeatureTab`, but `FeatureTab` (`Exclude<Tab, "help">`) is
 * WIDER than any single group's element type — `Tab` also carries tabs
 * that are reachable only as sub-tabs (`docker-logs`, `llm-stats`,
 * `network`, …). A type predicate has to narrow *to* its parameter's
 * type, so those predicates were unsound and rejected. Narrowing to
 * `GroupedTab` is both sound and assignable to `FeatureTab`, which is
 * what the callers actually need.
 */
type GroupedTab = (typeof TAB_GROUPS)[number]["tabs"][number];
type GroupedFeatureTab = Exclude<GroupedTab, "help">;

const FEATURE_TABS = new Set<string>(TAB_GROUPS.flatMap((g) => g.tabs).filter((t) => t !== "help"));

export function isFeatureTab(value: string): value is FeatureTab {
  return FEATURE_TABS.has(value);
}

export function isHelpOnlyTopic(value: string): value is HelpOnlyTopicId {
  return HELP_ONLY_TOPIC_IDS.has(value);
}

export function isHelpTopicId(value: string): value is HelpTopicId {
  return value === "intro" || isHelpOnlyTopic(value) || isFeatureTab(value);
}

function article(
  id: HelpTopicId,
  group: string,
  tagline: string,
  overview: string,
  steps: string[],
  tips: string[],
  openTab?: FeatureTab,
  images?: HelpArticleImage[],
): HelpArticle {
  const title =
    id === "intro"
      ? "Getting started"
      : id === "fleet-architecture"
        ? "How it works"
        : id === "fleet-projects"
          ? "Fleet Projects"
          : id === "fleet-work-plan"
            ? "Smart work plan"
            : id === "fleet-stop-resume"
              ? "Stop & resume"
              : id === "fleet-self-improvement"
                ? "Self improvement"
                : id === "fleet-self-improvement-local"
                  ? "Self improvement — local repo"
                  : id === "fleet-session-environments"
                    ? "Fleet session environments"
                    : titleForTab(id);
  return { id, group, title, tagline, overview, steps, tips, openTab, images };
}

export const HELP_ARTICLES: HelpArticle[] = [
  article(
    "intro",
    "Help",
    "In-app guides for every Fleet Control section.",
    "Fleet Control is the browser UI for your Jaisiu gateway. Use the left navigation to open each area, or pick a topic below to learn what it does and how to use it. Connect from Overview first if you see Offline in the header.",
    [
      "Open Overview and enter your gateway URL (usually ws://127.0.0.1:18789 or wss://your-host:18789).",
      "Paste the gateway token from onboarding or openclaw.json, then connect.",
      "Approve device pairing when prompted (jaisiu devices approve on the server).",
      "Browse topics in this Help section, then jump to a tab with Open section.",
      "Open How it works for the multi-agent glossary and fleet map architecture.",
    ],
    [
      "Local connections (127.0.0.1) are often auto-approved; LAN/Tailnet may need explicit approval.",
      "Docs: docs/web/control-ui.md in the Jaisiu repo for gateway URLs and auth details.",
      "Chat toolbar brain icon cycles assistant display: off → full → distraction-free (your messages + completion only).",
      "Subagent session tabs show a live Task / Now banner when opened from a parent run.",
    ],
  ),
  article(
    "fleet-architecture",
    "Help",
    "Agents, sessions, channels, skills, tools, and fleet map edges.",
    "Config defines who exists and how they connect; workspace files (SOUL.md, AGENTS.md) define behavior; sessions are live runs. The fleet map visualizes spawn routes, inbound bindings, and active subagents.",
    [
      "Read the glossary below for each term and edge type.",
      "Open Chat and expand Agent fleet map to apply topology, model, and behavior settings.",
      "Use the Fleet guide (? in the header) for a walkthrough with example tries.",
    ],
    [
      "Solid arrows = subagents.allowAgents; dashed = workforce.bindings; bright = live session only.",
      "Channel → agent bindings are configured under Channels/Config, not shown on the map yet.",
    ],
    "chat",
  ),
  article(
    "fleet-session-environments",
    "Help",
    "Per-session Docker sandboxes for MVP builds and tests.",
    "Each Fleet session tab can run tools in its own lightweight container (filesystem + processes isolated). Enable the MVP sandbox preset, build images on the gateway host, and use Session environment in Chat to inspect or recreate a tab.",
    [
      "Merge docs/config/fleet-mvp-sandbox-preset.json5 into gateway config and restart.",
      "Run scripts/helpers/sandbox-setup.sh and scripts/sandbox-common-setup.sh on the Docker host.",
      "Open Chat → Session environment → verify Sandbox (session) and Docker reachable.",
      "Use Recreate environment to wipe a broken MVP before retrying pytest or python main.py.",
      "Register MVP preview when a dev server listens in the sandbox (port 3000–9999); open the signed gateway URL.",
    ],
    [
      'Default config runs main session tools on the host — preset uses mode "all" and scope "session".',
      "Preview proxy requires bridge network and a process bound to 0.0.0.0 inside the container.",
    ],
    "chat",
  ),
  article(
    "fleet-projects",
    "Help",
    "Named code workspaces bound to chat sessions (per agent).",
    "Fleet Projects are scoped to the chat session that created them. Each project has a registry entry, a folder under .openclaw/fleet-projects/<slug>/, and a dedicated forked chat session.",
    [
      "Open Fleet Control → Projects tab → New project to fork a session and create an empty project root.",
      "Open a project to switch to its chat tab; use Project files tab to browse trees without leaving the parent chat.",
      "Write app code with write/edit tools into the project folder (not the main agent bootstrap files).",
      "Scan disk registers existing folders under .openclaw/fleet-projects/ that were created manually or by heartbeat.",
      "Delete project removes all files and deletes the linked session (confirm dialog).",
    ],
    [
      "Main heartbeat on agent:main:main often writes to the full agent workspace — Session output files stays empty unless tools target the session-output subfolder.",
      "For MVPs, enable the fleet MVP sandbox preset so each session tab has an isolated environment (see Fleet session environments).",
    ],
    "chat",
  ),
  article(
    "fleet-self-improvement",
    "Help",
    "One-click YouTrack task, code-fix worker, and feature-ship from Chat compose.",
    "Self improvement is a send option under the Fleet Control composer (not codebase-translate migration). When checked, your message creates one JAISIU YouTrack task, spawns jaisiu-code-fix-agent with jaisiu-self-improvement / feature-ship skills, and runs the implement → validate → push pipeline without a second Publish click.",
    [
      "Open Chat on the session that should own the work (main or a Fleet project session).",
      "Check Self improvement below the compose box (mutually exclusive with Orchestrate with @all).",
      "Optionally check Create new project first for an isolated .openclaw/fleet-projects tree, or pick Project to target an existing fork.",
      "Send a clear goal; the UI posts an ack with the YouTrack id and worker session key, then opens Fleet map.",
      "Ensure gateway host has YOUTRACK_URL (or YOUTRACK_BASE_URL) and YOUTRACK_TOKEN; register jaisiu-code-fix-agent with jaisiu-code-fix and jaisiu-feature-ship skills.",
      "Worker runs pnpm check/test, then scripts/jaisiu-feature-ship.py for the issue branch push.",
    ],
    [
      "Gateway RPCs: fleet.selfImprovement.start, status, list — jobs under .openclaw/self-improvement/jobs.json.",
      "For large epics with many subtasks, use Agents → Work plan instead (manual approve/publish per unit).",
      "Requires git push credentials on the machine running the worker agent.",
      "Local dev: see Help → Self improvement — local repo so the code-fix worker edits your git checkout.",
    ],
    "chat",
  ),
  article(
    "fleet-self-improvement-local",
    "Help",
    "Point agents at your Jaisiu git checkout so self-improvement edits show up in your repo.",
    "By default, jaisiu-code-fix-agent can run in an isolated sandbox under ~/.openclaw/workspace-jaisiu-code-fix-agent. Fleet self-improvement then writes files there, so git status in your real checkout stays empty. Set agents.defaults.repoRoot (and usually workspace) to your monorepo path in openclaw.json on the gateway host.",
    [
      "On the machine that runs the gateway, edit ~/.openclaw/openclaw.json (merged with any repo config overlay).",
      "Under agents.defaults, set repoRoot to the absolute path of your Jaisiu git checkout (directory that contains ui/vite.config.ts and package.json).",
      "Set workspace to the same path if the main agent still uses ~/.openclaw/workspace or another sandbox — both keys should reference the checkout you want tools to use.",
      "Restart the gateway after saving (e.g. docker restart for the gateway container, or jaisiu daemon restart locally).",
      "Send a small self-improvement task and confirm changes with git status in that checkout, not under ~/.openclaw/workspace-jaisiu-code-fix-agent.",
    ],
    [
      "Example (replace the path with your checkout):",
      '{ "agents": { "defaults": { "repoRoot": "/Users/you/Repo/jaisiu", "workspace": "/Users/you/Repo/jaisiu" } } }',
      "Self-improvement spawn pins workspaceDir to repoRoot (edits land in your checkout, not ~/.openclaw/workspace-jaisiu-code-fix-agent).",
      "Worker model: ollama/qwen3-coder:latest by default (override with JAISIU_SELF_IMPROVEMENT_MODEL on the gateway host).",
      "Jobs stay running until the worker subagent ends; status becomes shipped only when git shows real file changes in repoRoot (not grep-only chat).",
      "After Control UI code changes, run pnpm ui:build (or Developer → build) and refresh the browser.",
      "Docker: mount your host repo into the path you configure, or set repoRoot to the in-container mount (e.g. /home/node/.jaisiu/workspace/jaisiu).",
    ],
    "config",
  ),
  article(
    "fleet-work-plan",
    "Help",
    "Decompose epics into context-sized units, YouTrack subtasks, and subagent runs.",
    "Smart work plans live under Agents → Work plan. A planner agent breaks a goal into independent units with token-budget checks. Status pills show Planned, Backlog, In progress, In review, Paused, Blocked, Stopped, Done, Failed, and Cancelled. Blocked units and exhausted repair cycles appear in the Decisions inbox — resolve them before dispatch continues.",
    [
      "Open Agents → select the planner agent → Work plan tab.",
      "Enter a goal (use First …, then …, finally … for clearer splits) and Create plan.",
      "Review units: Context OK means the estimate fits the subagent budget; fix Review items before publish.",
      "Approve, then Publish YouTrack (gateway needs YOUTRACK_URL and YOUTRACK_TOKEN).",
      "Spawn only queued units (Planned / Backlog / Paused / Stopped). Done, failed, cancelled, and blocked units stay disabled until you retry or resolve a decision.",
      "When a plan badge says Needs decision, open it and use Unblock and re-queue or Clear decision gate.",
      "Autonomy badge: Manual blocks mutating shell and writes; Confirmation asks before shell/merge/push (workspace file writes stay free); Autonomous runs without asking.",
    ],
    [
      "Plans are stored per agent workspace under .openclaw/fleet-work-plans/registry.json.",
      "Gateway RPCs: fleet.workPlan.create, approve, publishYouTrack, spawnUnit, stop, resume, undo, listDecisions, resolveDecision.",
      "Attachment chips show Przetwarzanie / Gotowy / Nieobsługiwany / Błąd while files are classified.",
      "Subagents cannot spawn nested subagents; keep units one level deep.",
    ],
    "agents",
    [
      {
        file: "fleet-plan-status.webp",
        alt: "Work plan status dashboard with phase stepper and agent cards",
      },
      {
        file: "fleet-plan-control.webp",
        alt: "Work plan controls, decisions inbox, and Stop / Resume / Undo",
      },
    ],
  ),
  article(
    "fleet-stop-resume",
    "Help",
    "Stop preserves work; Resume continues a halted plan (Bible §11).",
    "Stop is plan-scoped: it halts live subagents, writes a checkpoint under .openclaw/fleet-checkpoints/, and marks running units as Stopped without deleting deliverables. Resume re-opens the plan for dispatch and re-queues stopped units. Restore checkpoint reloads the registry snapshot only — it does not revert git commits, YouTrack issues, or external APIs. Reset Node remains a whole-gateway action — use Stop when you only want to halt one plan.",
    [
      "Open Agents → Work plan → select an approved or published plan.",
      "Click Stop while units are running or between batches.",
      "Review unit rows: Running becomes Stopped; Done units stay Done. A checkpoint callout names when the snapshot was written.",
      "Click Resume when you want dispatch to continue (stopped units become Planned).",
      "Use Restore checkpoint only if you need the registry row from the last Stop — confirm the dialog; git and YouTrack stay as they are.",
      "If the plan shows Needs decision, automatic repair cycles are exhausted (§10) — resolve the inbox before resuming.",
    ],
    [
      "Checkpoints: .openclaw/fleet-checkpoints/<planId>.json in the shared workspace, including uncommitted file lists and carry directories.",
      "Agent path: plan_work(action=cancel) still cancels destructively; prefer fleet.workPlan.stop from the UI. Undo is plan_work(action=undo).",
    ],
    "agents",
    [
      {
        file: "fleet-plan-control.webp",
        alt: "Stop, Resume, and Restore checkpoint on the work plan execution bar",
      },
    ],
  ),
  article(
    "chat",
    "Fleet Control",
    subtitleForTab("chat"),
    "Fleet Control is the session-centric console: pick a session, message the agent, watch subagent runs, and edit fleet topology without leaving the browser.",
    [
      "Use session tabs or Sessions (nav) to switch conversations.",
      "Main tabs: Chat, Subagent activity (spawned runs: agent, task, status), Projects, Project files, Fleet map (delegation graph for this session).",
      "Fleet map: see parent → subagent tasks; Edit routes or agent/model from run rows.",
      "Type a message and send; use abort if a run should stop.",
      "Composer mode (toolbar or Shift+Tab): Agent (default), Ask (read-only), Plan, Debug, Multitask (@all). Mode persists in the browser.",
      "Plan mode: after the agent finishes a plan reply, use Implement above the composer to switch to Agent and start implementation (or Dismiss until the next plan).",
      "During implementation the agent may ask structured questions above the composer (blocking until you Continue) and maintain a live todo checklist via fleet_todo_write.",
      "During an active run, an activity strip above the composer lists recent file edits with 3-line diff previews; it disappears when the run completes. End-of-run file-change summaries are clickable for a full highlighted diff.",
      "Composer send options (Agent mode only): Orchestrate with @all, Create new project, Self improvement (YouTrack + code-fix + push), or Project target.",
      "Watch tool cards stream in the transcript; expand subagent blocks when delegating work.",
      "Toggle focus/thinking display from chat controls when you need a quieter view.",
      "Session navigator: + New agent / Agents on the agent row; + New session / Sessions on the actions row; open tabs with Fork and Reset. Classic layout vs Scrollable nav toggles where the navigator sits (above all tabs vs grow-down panel in Chat).",
    ],
    [
      "Session keys look like agent:main — match the session you configured in Config or Agents.",
      "Fleet Projects vs session output: Help → Fleet Projects.",
      "Fleet map tab shows session delegation (not the global agent topology editor — use Agents tab for that).",
      "Fleet glossary and architecture diagram: Help → How it works. Editor tabs: topology, config (model/tools/skills), behavior (SOUL.md).",
      "Closing a tab only hides it in the UI — use Delete session to abandon a session and its transcript.",
      "Context usage % updates after agent replies (not while you type).",
      "If disconnected, fix gateway URL/token on Overview before debugging chat itself.",
    ],
    "chat",
  ),
  article(
    "overview",
    "Control",
    subtitleForTab("overview"),
    "Overview is your health dashboard: connection status, uptime, counts, and quick entry points before you dive into channels or agents.",
    [
      "Confirm Jaisiu shows Online after connecting.",
      "Review presence/session/cron summaries for a fast sanity check.",
      "Use Google Drive integration controls here when EFSS backup is configured.",
      "Refresh when you have restarted the gateway or changed network access.",
    ],
    ["Start here after every gateway restart or token rotation."],
    "overview",
  ),
  article(
    "channels",
    "Control",
    subtitleForTab("channels"),
    "Channels lists messaging integrations (Telegram, WhatsApp, Discord, Slack, plugins) and their login/state.",
    [
      "Open Channels and wait for channels.status to load.",
      "Use QR or login flows shown for each channel type.",
      "Patch per-channel settings when the UI exposes config fields.",
      "Return to Chat to verify inbound messages reach the gateway.",
    ],
    [
      "409 Telegram errors often mean a second bot instance is running elsewhere.",
      "Add allowFrom for your user ID in openclaw.json when commands are unauthorized.",
    ],
    "channels",
  ),
  article(
    "instances",
    "Control",
    subtitleForTab("instances"),
    "Instances shows presence beacons from connected clients and nodes — who is online and reporting heartbeat.",
    [
      "Open Instances and refresh to pull system-presence.",
      "Compare instance IDs with Nodes when pairing devices.",
      "Use counts on Overview to see if expected clients are connected.",
    ],
    ["Stale entries usually clear after disconnect or gateway restart."],
    "instances",
  ),
  article(
    "sessions",
    "Control",
    subtitleForTab("sessions"),
    "Sessions lists active agent sessions and lets you adjust per-session defaults such as thinking/verbose levels.",
    [
      "Browse the session list and note each session key.",
      "Open Chat links from a row to jump straight into that session.",
      "Patch session fields the gateway exposes (thinking level, verbosity).",
    ],
    ["Session keys propagate to Chat and Cron when scheduling runs."],
    "sessions",
  ),
  article(
    "llm",
    "Control",
    subtitleForTab("llm"),
    "LLM unifies session usage, fleet network spend, local Ollama capacity, and a live local-LLM console. The tabs along the top switch between Overview, Analytics, Fleet spend, Sessions, Trace, and Local LLM.",
    [
      "Open LLM and use the tabs along the top to switch between Overview, Analytics, Fleet spend, Sessions, Trace, and Local LLM.",
      "Overview: Local capacity card at the top polls every 60s while the tab is open — CPU %, memory, Ollama loaded models, active agent runs vs configured max concurrent.",
      "Use the heuristic recommendation (~N more chat slots) before spawning more parallel local agents; refresh manually anytime.",
      "Analytics / Fleet spend / Sessions / Trace work the same as before; Session navigator scopes stats by agent → kind → id.",
      "Local LLM tab is a read-only Ollama console: model status (idle, loaded, in use), live inference lines, gateway log tail, bottleneck strip, performance factors table.",
      "Auto-refresh on Local LLM uses 1s while a run is streaming and 10s when idle.",
      "For native thinking traces: set session Reasoning to On or Stream (Sessions or /reasoning stream), then use Chat → Thinking activity; live thinking preview appears on the Local LLM tab.",
    ],
    [
      "Local capacity probes run on the gateway host (same machine as Ollama when using 127.0.0.1). Remote Ollama GPUs are not measured here.",
      "On Apple Silicon, RAM % is a proxy — there is no per-model VRAM readout; treat estimates as conservative.",
      "OLLAMA_NUM_PARALLEL and gateway maxConcurrent are separate limits; the bottlenecks list shows which constraint binds.",
      "If Ollama is unreachable on the Local LLM tab, start `ollama serve` on the gateway host or fix OLLAMA_HOST in Docker.",
      "Unknown method `resources.local` means rebuild and restart the gateway after upgrading.",
      "Offline nodes may show missing spend until they report billing metadata.",
    ],
    "llm",
    [
      {
        file: "llm-connect-dashboard.webp",
        alt: "LLM Connect providers list with connection status",
      },
      {
        file: "llm-connect-settings.webp",
        alt: "LLM Connect API key, test, and default-model settings",
      },
      {
        file: "llm-connect-local.webp",
        alt: "Local LLM console for Ollama capacity and live inference",
      },
    ],
  ),
  article(
    "cron",
    "Control",
    subtitleForTab("cron"),
    "Cron schedules wakeups and recurring agent runs against the gateway scheduler.",
    [
      "List jobs and confirm enabled/disabled state.",
      "Add a job with session key, schedule, and payload the gateway expects.",
      "Run now to test; open run history for failures.",
      "Jump to Chat via session links on a job row when debugging output.",
    ],
    ["Cron uses gateway time; verify timezone on the host if schedules fire early/late."],
    "cron",
  ),
  article(
    "agents",
    "Agent",
    subtitleForTab("agents"),
    "Agents manages workspaces, tools, identities, and per-agent panels (files, tools, skills, channels, cron, work plan).",
    [
      "Select an agent from the list; review overview metadata.",
      "Switch side panels for files, tools, skills, channels, cron, or Work plan as needed.",
      "Work plan: decompose goals, publish YouTrack subtasks, spawn subagents per unit (Help → Smart work plan).",
      "Edit identity or workspace settings the UI exposes, then save via gateway RPC.",
      "Open Chat with that agent's main session to test changes.",
    ],
    [
      "Config and Agents overlap — large changes may be easier in Config raw JSON.",
      "Work plan publish requires YouTrack env on the gateway host.",
    ],
    "agents",
    [
      {
        file: "agents-agent-config.webp",
        alt: "Agent overview and configuration",
      },
      {
        file: "agents-agent-files.webp",
        alt: "Agent files panel",
      },
      {
        file: "agents-agent-advanced-tab.webp",
        alt: "Agent advanced settings",
      },
    ],
  ),
  article(
    "skills",
    "Agent",
    subtitleForTab("skills"),
    "Skills controls which skills are available to agents and injects API keys where required.",
    [
      "Review installed vs available skills.",
      "Enable or disable skills per deployment policy.",
      "Install or update skills when the gateway supports it.",
      "Provide API keys only through the secure fields shown (not chat).",
    ],
    ["Skills also appear under Agents panel for a single agent context."],
    "skills",
    [
      {
        file: "agents-agents-skills.webp",
        alt: "Agent skills panel",
      },
    ],
  ),
  article(
    "instances",
    "Agent",
    subtitleForTab("instances"),
    "Instances lists paired devices, capabilities, and which commands they expose to the gateway. Presence beacons from connected clients are also displayed here.",
    [
      "Refresh node.list results.",
      "Compare capabilities with exec approval policies.",
      "Pair new devices using the gateway CLI flow documented for your OS.",
      "Connected Instances shows live presence beacons from the gateway and clients.",
    ],
    ["Exec approvals may be edited on this same page under the Exec Approvals card."],
    "instances",
  ),
  article(
    "employees",
    "Agent",
    subtitleForTab("employees"),
    "Employees manages virtual employees and HR sync — mapping people records to agent identities.",
    [
      "Browse employees and open a detail row.",
      "Sync or refresh HR data when connectors are configured.",
      "Launch Chat for an employee's agent session from detail actions.",
    ],
    ["Employee routes use /employees/:id paths; Help links back to the list tab."],
    "employees",
  ),
  article(
    "workforce",
    "Agent",
    subtitleForTab("workforce"),
    "Workforce designs automation flows: skills, routing rules, delegation, and analytics across the fleet.",
    [
      "Open Workforce to load flow definitions from the gateway.",
      "Edit routing/delegation rules in the UI controls provided.",
      "Use shortcuts to Employees or Config when wiring new flows.",
      "Validate in Chat with a delegate-friendly prompt.",
    ],
    ["Mobile bottom nav includes Workforce for quick fleet checks on phone."],
    "workforce",
  ),
  article(
    "config",
    "Settings",
    subtitleForTab("config"),
    "Config edits openclaw.json safely with schema-aware forms plus a raw JSON editor.",
    [
      "Load config and schema; prefer form mode for guided edits.",
      "Apply changes; gateway validates and may restart.",
      "Use base-hash guards — reload if concurrent edits occurred.",
      "Restart gateway from host when Docker/LaunchAgent is your runtime.",
    ],
    ["Never paste secrets into chat; use config fields or env on the server."],
    "config",
  ),
  article(
    "developer",
    "Settings",
    subtitleForTab("developer"),
    "Developer runs Control UI builds on the gateway host and streams Vite output in the browser.",
    [
      "Open Developer and click Build Control UI.",
      "Watch the terminal output for errors.",
      "Reload the page after a successful build to load new assets.",
    ],
    [
      "Requires a Jaisiu checkout on the gateway machine (not just a global install without sources).",
      "Docker deployments may need host-side `scripts/jaisiu-ui-sync.sh` instead.",
    ],
    "developer",
  ),
  article(
    "debug",
    "Settings",
    subtitleForTab("debug"),
    "Debug exposes gateway snapshots, event streams, and manual RPC calls for operators.",
    [
      "Inspect status/health/models snapshots.",
      "Tail the event log buffer for WS traffic issues.",
      "Send manual RPC with JSON payloads when reproducing bugs.",
    ],
    ["Stop Debug polling when leaving the tab to reduce background load."],
    "debug",
  ),
  article(
    "logs",
    "Settings",
    subtitleForTab("logs"),
    "Logs tails gateway file logs with filtering and export — best for stack traces and restart issues.",
    [
      "Open Logs and confirm tail is running.",
      "Filter by level or substring when hunting errors.",
      "Export a chunk when attaching evidence to YouTrack.",
    ],
    ["Pair with Debug events when you need both file logs and live WS traces."],
    "logs",
  ),
  article(
    "network",
    "Network",
    subtitleForTab("network"),
    "Network discovers and compares other Jaisiu instances that share the registry, so you can see peers and pair across deployments.",
    [
      "Open Network to load the registry response from the gateway.",
      "Compare peer endpoints, versions, and reachability.",
      "Drill into a peer for routes, skills, or session hints when present.",
      "Refresh after changing registry credentials on the host.",
    ],
    ["Network only shows peers that publish to the same registry the gateway is configured for."],
    "network",
  ),
  article(
    "openclaw-logs",
    "Settings",
    subtitleForTab("openclaw-logs"),
    "OpenClaw Logs tails session output files under ~/.openclaw, useful when chat output doesn't surface in the UI.",
    [
      "Open OpenClaw Logs and pick the session folder to inspect.",
      "Tail the latest log file or open older ones for context.",
      "Filter by level when chasing crashes.",
      "Export a chunk to attach to a YouTrack issue.",
    ],
    ["Each session keeps its own folder; the tab lists them as they appear on disk."],
    "openclaw-logs",
  ),
  article(
    "jaisiu-logs",
    "Settings",
    subtitleForTab("jaisiu-logs"),
    "Jaisiu Logs tails session output files under ~/.jaisiu — the alternate state root used on some deployments.",
    [
      "Open Jaisiu Logs to list session folders under ~/.jaisiu.",
      "Tail the latest log file or open older ones for context.",
      "Filter by level when chasing crashes.",
      "Confirm which state root your deployment uses by reading the gateway env.",
    ],
    ["Tabs mirror OpenClaw Logs but read from ~/.jaisiu; pick the right root for your host."],
    "jaisiu-logs",
  ),
  article(
    "gateway-logs",
    "Settings",
    subtitleForTab("gateway-logs"),
    "Gateway Logs is an alternate view of the gateway file logs when the primary Logs tab is busy or filtered out the lines you need.",
    [
      "Open Gateway Logs to confirm the tail is connected.",
      "Use a different filter set than the primary Logs tab when isolating an issue.",
      "Export a chunk when attaching evidence to YouTrack.",
    ],
    ["Switch back to Logs for the canonical tail; this tab is the secondary view."],
    "gateway-logs",
  ),
  article(
    "docker-logs",
    "Settings",
    subtitleForTab("docker-logs"),
    "Docker Logs streams live container logs from the gateway host so you can watch stdout/stderr without SSH.",
    [
      "Open Docker Logs and pick the container (gateway, broker, redis, efss).",
      "Stream the tail; pause when you need to read a frame.",
      "Filter by substring when matching a specific error.",
      "Restart a container from Overview or the host when the tail shows a crash loop.",
    ],
    ["Requires the gateway to have docker socket access on the host."],
    "docker-logs",
  ),
];

const ARTICLE_BY_ID = new Map<HelpTopicId, HelpArticle>(HELP_ARTICLES.map((a) => [a.id, a]));

export function getHelpArticle(id: HelpTopicId): HelpArticle {
  return ARTICLE_BY_ID.get(id) ?? ARTICLE_BY_ID.get("intro")!;
}

export function listHelpTopicsGrouped(): Array<{ label: string; topics: HelpTopicId[] }> {
  const intro: HelpTopicId[] = [
    "intro",
    "fleet-architecture",
    "fleet-session-environments",
    "fleet-self-improvement",
    "fleet-self-improvement-local",
  ];
  const groups = TAB_GROUPS.map((g) => ({
    label: g.label,
    topics: g.tabs.filter((t): t is GroupedFeatureTab => t !== "help"),
  })).filter((g) => g.topics.length > 0);
  return [{ label: "Help", topics: intro }, ...groups];
}
